import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogService } from "@cervval.js/common/src/services/log.service";
import { TsDate, UnixTimestamp, UnixTimestampIndeterminateInterval, add, getTodayDate, isBetween, isInside, subtract, toDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { CalendarOptions, CjsCalendar, DAY_CELL_HIGHLIGHT, DAY_CELL_HIGHLIGHT_OTHER_MONTH, DAY_CELL_IS_CLICKED, DAY_CELL_OTHER_MONTH, DAY_CELL_ROUNDED, DAY_CELL_TODAY, HTMLDateDivElement, defaultCalendarOptions } from "../calendar/calendar";

/** Parameters for DatePicker */
export interface DatePickerOptions extends CalendarOptions {

    /** Date selection parameters */
    /** Valid range intervals
     * If empty, all dates are valid
     */
    ranges: UnixTimestampIndeterminateInterval[];

    /**
     * Other month cells "select date" behavior is replaced by "change month" behavior
     */
    enableOtherMonthCellChangeMonth: boolean;

    /**
     * Enable keyboard inputs for component (up, left, right, up, enter keys input events)
     */
    enableKeyboardInput: boolean;
}

/** Parameters for DatePicker */
export const defaultDatePickerOptions: DatePickerOptions = {

    ...defaultCalendarOptions,

    /** Date selection parameters */
    /** Valid range, if empty, all dates are valid */
    ranges: [],

    /**
     * Other month cells "select date" behavior is replaced by "change month" behavior
     */
    enableOtherMonthCellChangeMonth: false,

    /**
     * Enable keyboard inputs for component (up, left, right, up, enter keys input events)
     */
    enableKeyboardInput: true
};

// Class names constants
export const DAY_CELL_SELECTABLE = "selectable";
const DAY_CELL_HIGHLIGHT_UNSELECTABLE = "highlight-unselectable";
const DAY_CELL_HIGHLIGHT_OTHER_MONTH_UNSELECTABLE = "highlight-other-month-unselectable";
const UNSELECTABLE = "unselectable";
const UNSELECTABLE_OTHER_MONTH = "unselectable-other-month";

@CustomElement({
    name: "cjs-date-picker",
    style: require("./datePicker.scss"),
    elementCssClasses: ["cjs-calendar"]
    // Uses the same template as calendar (extends)
})
export class CjsDatePicker extends CjsCalendar {

    @TypedWebComponentOptions(defaultDatePickerOptions)
    public override readonly options!: WebComponentOptions<DatePickerOptions>;

    /** The current cell clicked */
    protected _clickedCell: HTMLDateDivElement | null = null;

    /** The current cell preselected by user keyboard input */
    protected _keyboardPreselectedCell: HTMLDateDivElement | null = null;

    constructor(options?: Partial<DatePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(options, customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** 
     * Refresh component, re-render component with options
     */
    public override refresh(): void {
        super.refresh();

        // Add keyboard input listener
        if (this.options.enableKeyboardInput) {
            document.body.addEventListener("keydown", this._onKeyboardDown, true);
        }

        if (!this.options.enableKeyboardInput) {
            document.body.removeEventListener("keydown", this._onKeyboardDown, true);
        }
    }

    /** Triggered when the element is removed from DOM */
    public disconnectedCallback(): void {
        if (this.options.enableKeyboardInput) {
            document.body.removeEventListener("keydown", this._onKeyboardDown, true);
        }
    }


    /**
     * Set value of component
     */
    public override setValue(ts: UnixTimestamp | undefined): void {
        this._value = ts;

        if (!ts) {
            this._clickedCell = null;
            this.refresh();
            return;
        }

        // If value is not in range, return
        if (this._isDateOuterSelectedBound(ts)) {
            getLogService().warn(`Could not set value ${ts}: value out of range`);
            return;
        }

        // Clean previous cell style
        this._clickedCell?.classList.remove(DAY_CELL_IS_CLICKED);

        // Update selected date & cell
        this._clickedCell = this._getCellByDate(ts);
        this._clickedCell?.classList.add(DAY_CELL_IS_CLICKED);
        this.setGridByTs(ts);
    }

    /** Init containers listeners */
    protected override _initListeners(): void {
        super._initListeners();
    }

    /** 
     * Set up days grid for DatePickerComponent
     */
    protected override _setupDaysGrid(): void {
        super._setupDaysGrid();

        const currentDate = this.currentDate;
        for (const dayCell of this.dayCells.filter(dayCell => dayCell.date.yearAndMonth === currentDate.yearAndMonth)) {
            // Add behaviors and display
            this._addCellClickListener(dayCell);
        }

    }

    /** On keyboard key is pressed down event */
    protected _onKeyboardDown: EventListenerObject = {
        handleEvent: (evt: KeyboardEvent): void => {
            if (this.disabled) { return; }// Don't allow keyboardDown callback if component is disabled

            // init x (-1: left / +1: right) and y (-1: down / +1: up)
            let x = 0;
            let y = 0;

            // Get origin date: select in this order: _keyboardPreselectedCell => selectedDate => currentDate
            const fromDate: TsDate = this._keyboardPreselectedCell ? this._keyboardPreselectedCell.date : this._value ?
                toDate(this._value) : this.currentDate;

            // Switch on key input
            switch (evt.key) {
                case KeyboardInput.ARROW_DOWN:
                    y = +1;
                    break;
                case KeyboardInput.ARROW_RIGHT:
                    x = +1;
                    break;
                case KeyboardInput.ARROW_UP:
                    y = -1;
                    break;
                case KeyboardInput.ARROW_LEFT:
                    x = -1;
                    break;
                case KeyboardInput.ENTER:
                    if (this._keyboardPreselectedCell) {

                        // Reset keyboard cell display
                        this._keyboardPreselectedCell.classList.remove("preselected");

                        // Simulate user click
                        this._cellClickCallback(this._keyboardPreselectedCell);
                    }
                    return;
                default:
                    return;
            }

            // Get the date to go on based on key input
            const toDateAdd: TsDate = add(fromDate, { day: x * 1 + y * 7 });

            // Add change month behavior if not destination date isn't same month and a directional key is pressed
            if (toDateAdd.yearAndMonth !== this.currentDate.yearAndMonth && (x !== 0 || y !== 0)) {
                this._changeMonthCallback(x > 0 || y > 0 ? 1 : -1); // Change month (=~ cells displayed)
            }

            // Retrieve destination cell
            const toCell: HTMLDateDivElement | null = this._getCellByDate(toDateAdd.uts);

            if (toCell) {

                // Remove the display for previous preselected cell
                if (this._keyboardPreselectedCell) { this._keyboardPreselectedCell.classList.remove("preselected"); }

                // Add display for destination cell
                toCell.classList.add("preselected");

                // Update value
                this._keyboardPreselectedCell = toCell;
            }
        }
    };

    /**
     * Create a cell of an other month than the current one
     *
     * @param dayOffset Offset of days from
     * @param monthOffset Offset of month from 
     */
    protected override _createOtherMonthCell(dayOffset: number, monthOffset: number): HTMLDateDivElement {

        // Create cell
        const dayCell = super._createOtherMonthCell(dayOffset, monthOffset);

        if (this.options.displayOtherMonthsDays) {
            // Add click behavior
            this._addCellClickListener(dayCell);
        }

        return dayCell;
    }


    /**
     * Set date cells behaviors and displays
     */
    protected override _setCellDisplay(cell: HTMLDateDivElement): void {

        // Set up border radius display
        toggleClass(cell, this.options.roundedCells, DAY_CELL_ROUNDED);

        const isDateOuterSelectedBound = this._isDateOuterSelectedBound(cell.date.uts);

        // Highlight weekend cells days
        if (this.options.highLightDays?.includes(cell.date.weekday)) {
            // Determine witch class to add to cell
            let classToAdd = "";
            if (cell.classList.contains(DAY_CELL_OTHER_MONTH)) {
                classToAdd = isDateOuterSelectedBound ? DAY_CELL_HIGHLIGHT_OTHER_MONTH_UNSELECTABLE : DAY_CELL_HIGHLIGHT_OTHER_MONTH;
            } else {
                classToAdd = isDateOuterSelectedBound ? DAY_CELL_HIGHLIGHT_UNSELECTABLE : DAY_CELL_HIGHLIGHT;
            }

            // Add class from highlighDays params
            cell.classList.add(classToAdd);
        }
        // Set calendar week display
        this.setCalendarWeekCellDisplay(cell);

        // Today date display
        if (cell.date.yearAndOrdinal === getTodayDate().yearAndOrdinal && this.options.displayTodayDate) {
            cell.classList.add(DAY_CELL_TODAY);
        }

        // For other month cells, add a change month behavior
        if (this._isOtherMonthCell(cell) && !isDateOuterSelectedBound && this.options.enableOtherMonthCellChangeMonth) {
            this._addChangeMonthClickListener(cell, cell.date);
        }

        // If the selectedDate is equal to cellDate, add isClicked display
        if (this._value && toDate(this._value).yearAndOrdinal === cell.date.yearAndOrdinal) {
            cell.classList.add(DAY_CELL_IS_CLICKED);
            this._clickedCell = cell;
        } else {
            cell.classList.remove(DAY_CELL_IS_CLICKED);
        }

    }

    /**
     * Add a click listener to a cell, used to switch between selected cells for component
     * @param cell Cell to set click listener
     */
    protected _addCellClickListener(cell: HTMLDateDivElement): void {


        // Add css class for hover and cursor
        cell.classList.add(DAY_CELL_SELECTABLE);

        // Check if the cell can be clickable/selected
        if (this._isDateOuterSelectedBound(cell.date.uts)) {
            const _isOtherMonthCell = this._isOtherMonthCell(cell);
            let classToAdd = "";

            if (_isOtherMonthCell) {
                classToAdd = UNSELECTABLE_OTHER_MONTH;
                cell.classList.remove(DAY_CELL_OTHER_MONTH);
                cell.classList.remove(DAY_CELL_SELECTABLE);
            } else {
                classToAdd = UNSELECTABLE;
                cell.classList.remove(DAY_CELL_SELECTABLE);
            }

            cell.classList.add(classToAdd);
            return;
        }

        // Add click listener, toggle "clicked" class and update the selectedDate
        cell.addEventListener("click", () => {
            this._cellClickCallback(cell);
        });
    }

    /**
     * Behavior when cell is clicked by user
     */
    protected _cellClickCallback(cell: HTMLDateDivElement): void {

        if (this.disabled) { return; }// Don't allow cellClick callback if component is disabled

        // Update selected date & cell
        if (this._value && cell.date.yearAndOrdinal === toDate(this._value).yearAndOrdinal) { return; }

        if (this._clickedCell != null) {
            this._clickedCell.classList.remove(DAY_CELL_IS_CLICKED);
        } else {
            // Remove IS_CLICKED class of cells
            this.dayCells.forEach(dayCell => dayCell.classList.remove(DAY_CELL_IS_CLICKED));
        }

        cell.classList.add(DAY_CELL_IS_CLICKED);

        this._clickedCell = cell;
        this._value = cell.date.uts;
        this.selectedDateChange();
        this._keyboardPreselectedCell = null;

    }

    /**
     * change month behavior for an element
     * @param offset Month offset, can be a number (-1/+1) or a date from a different month than current month displayed
     * FIXME above comment is not correct offset must be a number
     * @param monthYearDiv [optional] The div to set textContent 
     */
    protected override _changeMonthCallback(offset: number | TsDate, monthYearDiv?: HTMLElement | null): void {

        super._changeMonthCallback(offset, monthYearDiv);
        // Reset clickedCell
        this._clickedCell = null;
    }

    /** When selected date is updated */
    public selectedDateChange(): void {
        this._dispatchOnPickerValueChange();
    }

    /**
     * Check if a date is outer selectStartDate and selectEndDate range
     * @param date Date to check
     */
    protected _isDateOuterSelectedBound(date: UnixTimestamp): boolean {

        // If ranges are defined, check if date is in one range
        if (this.options.ranges.length) {
            for (const range of this.options.ranges) {
                if (this._isDateInRange(date, range)) { return false; }
            }
            // Return true only if date is not in a range interval
            return true;
        }

        // If there is no range, all dates are valid
        return false;
    }

    /**
     * Return true if the given date in include in range
     * @param date date to check
     * @param range range to check in
     */
    protected _isDateInRange(date: UnixTimestamp, range: UnixTimestampIndeterminateInterval): boolean {
        return this instanceof CjsDatePicker ? isBetween(date, range.start, range.finish, true) : isInside(date, range.start, range.finish);// isInside for DateIntervalPicker
    }

    /**
     * Get a cell div element from his date value
     * @param ts value of date to get corresponding cell
     */
    protected _getCellByDate(ts: UnixTimestamp, p?: { isFinishDate: boolean }): HTMLDateDivElement | null {
        if (this.components.dayGrid) {

            // Get all calendar cells divs
            const childrens: HTMLCollection = this.components.dayGrid.children;

            for (let i = 0; i < childrens.length; i++) {
                // Check if cell date is equal to date parameter getter
                const cell: HTMLDateDivElement = childrens[i] as HTMLDateDivElement;

                let tsToRetrieve = ts;
                if (p?.isFinishDate) {// Remove one day to date to retrieve correct cell (used in dateIntervalPicker)
                    tsToRetrieve = subtract(ts, { day: 1 }).uts;
                }
                if (cell.date.uts === tsToRetrieve) {
                    return cell;
                }
            }

        }
        return null;
    }

    /** Reset date to today */
    protected override _resetTodayDate(): void {
        super._resetTodayDate();

        // Dispatch change event
        this.selectedDateChange();
    }
}